import { CheckIcon } from 'lucide-react';
import { Select as SelectPrimitive } from 'radix-ui';
import { type ComponentPropsWithoutRef, type FC } from 'react';
import { portaledSurfaceVariants, type PortaledSurfaceVariant } from '../lib/portaledSurface';
import { cn } from '../lib/utils';
import {
  SelectContent as ShadcnSelectContent,
  SelectTrigger as ShadcnSelectTrigger,
} from './ui/select';

type SelectTriggerProps = ComponentPropsWithoutRef<typeof ShadcnSelectTrigger>;

export const SelectTrigger: FC<SelectTriggerProps> = (props) => {
  return (
    <ShadcnSelectTrigger
      {...props}
      className={cn(
        `overflow-hidden text-ellipsis body-md-strong [&_svg:not([class*='text-'])]:text-foreground-primary [&_svg:not([class*='text-'])]:opacity-100`,
        // Override ui/select `dark:bg-input/30` + hover `/50` → Figma `background/surface`.
        'dark:bg-background-surface dark:hover:bg-background-surface',
        props.className,
      )}
    />
  );
};

type SelectContentProps = ComponentPropsWithoutRef<typeof ShadcnSelectContent> & {
  /** Light (default) or dark surface. Pass `"dark"` for a select opened from inside a `DarkThemeScope` — it portals to `document.body` and so escapes the scope's own `.dark`. */
  variant?: PortaledSurfaceVariant;
};

export const SelectContent: FC<SelectContentProps> = ({
  variant = 'light',
  className,
  ...props
}) => {
  return (
    <ShadcnSelectContent
      {...props}
      className={cn('tw', portaledSurfaceVariants({ variant }), className)}
    />
  );
};

type SelectMenuItemProps = Omit<
  ComponentPropsWithoutRef<typeof SelectPrimitive.Item>,
  'children'
> & {
  label: string;
  description?: string;
  checkbox?: boolean;
};

/**
 * Visual-only checkbox indicator that reflects the parent SelectItem's
 * selected state via the `data-[state=checked]` attribute on the Item.
 */
const SelectCheckbox: FC = () => (
  <span
    aria-hidden
    className={cn(
      'shadow-xs flex size-4 shrink-0 items-center justify-center rounded border border-input transition-shadow',
      'group-data-[state=checked]:border-primary group-data-[state=checked]:bg-primary group-data-[state=checked]:text-primary-foreground',
    )}
  >
    <CheckIcon className="size-3.5 opacity-0 group-data-[state=checked]:opacity-100" />
  </span>
);

export const SelectMenuItem: FC<SelectMenuItemProps> = ({
  className,
  label,
  description,
  checkbox,
  ...props
}) => {
  return (
    <SelectPrimitive.Item
      data-slot="select-menu-item"
      className={cn(
        'outline-hidden group relative flex w-full cursor-default select-none items-center gap-2 rounded p-2 text-sm focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        !checkbox && 'pr-8',
        className,
      )}
      {...props}
    >
      {checkbox && <SelectCheckbox />}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <SelectPrimitive.ItemText>
          <span className="text-foreground-primary label-md">{label}</span>
        </SelectPrimitive.ItemText>
        {description && <span className="text-foreground-secondary caption">{description}</span>}
      </div>
      {!checkbox && (
        <span className="absolute right-2 flex size-3.5 items-center justify-center">
          <SelectPrimitive.ItemIndicator>
            <CheckIcon className="size-4" />
          </SelectPrimitive.ItemIndicator>
        </span>
      )}
    </SelectPrimitive.Item>
  );
};

export {
  Select,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectValue,
} from './ui/select';

export type { PortaledSurfaceVariant as SelectSurfaceVariant };
